import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { toast } from 'sonner';

import { calendarDay, fullDate, localDay } from '@app/utils/helpers/date-utils';
import { PendingAbsencesContext } from '@app/components/shared/sprintDayReport/pendingAbsences';
import { PhilBotIcon } from '@app/components/core/philBot/PhilBotIcon';
import { TabBar } from '@app/components/core/tab-bar';
import { tabClass } from '@app/constants/styles';
import { ProductStatus, ReportEntity } from '@data';
import { useOrganizationChapters } from '@data/hooks/organization';
import { useAiSummaryInstructions, useOwnMembership, useSetDailyNudge } from '@data/hooks/membership';
import { useDailyReportDefaults, useDailyReportDraft } from '@data/hooks/reports';
import { useListProducts } from '@data/hooks/products';
import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { cn } from '@ui/lib/utils';
import { Label } from '@ui/components/ui/label';
import { Textarea } from '@ui/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/ui/tooltip';

import { isAbsenceProduct } from '@app/components/shared/sprintDayReport/absenceProducts';

import ContinueButton, { WritingMode } from './ContinueButton';
import Presence from './Presence';
import DraftSettings from './DraftSettings';
import { draftingLineFor } from './draftingLine';
import ProductPicker from './ProductPicker';
import { useReadingSources } from './readingSources';
import ReportIncidentForm from './ReportIncidentForm';
import { checkHours, HoursErrors, targetAfterTimeOff, withAutofilledHours } from './validateHours';

export interface PhillReportDialogProps {
  show: boolean;
  onHide: () => void;
  organizationId: string;
  /** Whose day this is. The report endpoint takes it explicitly; it is not inferred from the token. */
  userId: string;
  /** Called once per product. Must reject if the write failed, so the dialog stays open with the descriptions. */
  onSubmit: (values: Partial<ReportEntity>) => Promise<unknown>;
  /** The day being reported, as a local date. */
  date: Date;
  /** What the day already holds, so the hours aim at what is left. */
  reportedMinutes?: number;
  /** Products already holding ordinary work that day: a second entry would split the day's line. */
  reportedProductIds?: string[];
}

type Step = 'hours' | 'drafting' | 'review';
type Tab = 'work' | 'incident';

const STEP_ORDER: Record<Step, number> = { hours: 0, drafting: 1, review: 2 };

const STEP_ANIMATION: Record<'forward' | 'back', string> = {
  forward: 'animate-report-step-forward',
  back: 'animate-report-step-back',
};

const hoursLabel = (hours: number): string => String(Math.round(hours * 100) / 100);

/** A day's report, opened from that day's plus: products and hours, then Phill's draft or your own lines. */
const PhillReportDialog = ({
  show,
  onHide,
  organizationId,
  userId,
  onSubmit,
  date,
  reportedMinutes = 0,
  reportedProductIds,
}: PhillReportDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const { aiSummaries, membership } = useOwnMembership(organizationId, userId);
  const { data: ownInstructions } = useAiSummaryInstructions(membership?.id);

  const workday = useMemo(() => calendarDay(date), [date]);
  const day = fullDate(workday);

  const [tab, setTab] = useState<Tab>('work');
  const [step, setStep] = useState<Step>('hours');
  // Set where the change is caused, not derived: Back and Continue both land on steps reachable either way.
  const [stepDirection, setStepDirection] = useState<'forward' | 'back' | null>(null);
  const [splitOpen, setSplitOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [hours, setHours] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<HoursErrors>({});
  const [note, setNote] = useState<string | undefined>();
  const [saved, setSaved] = useState<string | undefined>();
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});
  // What the draft returned, not what is in the fields now: typing a line must not rewrite why they started empty.
  const [draftedNothing, setDraftedNothing] = useState(false);
  const [chapterKey, setChapterKey] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const incidentHours = useRef<number | null>(null);

  const { data: productData } = useListProducts(organizationId, {
    statuses: [ProductStatus.active, ProductStatus.support],
  });
  const { data: defaults } = useDailyReportDefaults(organizationId, show);
  const { mutateAsync: draftReport } = useDailyReportDraft();

  // The defaults answer with the chapter's key; on screen it reads as the label people know, or the key if the list lacks it.
  const { data: chapters } = useOrganizationChapters(show ? organizationId : undefined);
  const chapterName = useMemo(
    () => (chapters?.items ?? []).find(candidate => candidate.key === chapterKey)?.label ?? chapterKey,
    [chapters, chapterKey],
  );

  const products = useMemo(() => productData?.items ?? [], [productData]);
  const taken = useMemo(
    () => products.filter(product => reportedProductIds?.includes(product.id)).map(product => product.name),
    [products, reportedProductIds],
  );
  const open = useMemo(
    () => products.map(product => product.name).filter(name => !taken.includes(name)),
    [products, taken],
  );
  const pills = useMemo(
    () => (defaults?.teamProducts ?? []).filter(name => products.some(product => product.name === name)),
    [defaults, products],
  );
  // The hours and the review follow the pills, whatever order they were clicked in; dropdown picks come last.
  const ordered = useMemo(
    () => [
      ...pills.filter(product => selected.includes(product)),
      ...selected.filter(product => !pills.includes(product)),
    ],
    [pills, selected],
  );

  const readingSources = useReadingSources(products, selected, ownInstructions);
  const sourcesRead = readingSources.filter(source => source.reads);
  // Picked under the caret, shown at once and kept as the setting, so the next report opens the same way.
  const [chosenMode, setChosenMode] = useState<WritingMode>();
  const { mutateAsync: saveDrafting } = useSetDailyNudge(membership?.id);
  const chooseMode = (next: WritingMode): void => {
    setChosenMode(next);
    saveDrafting(next === 'ai').catch(() => setChosenMode(undefined));
  };
  // The Incident tab grows while it logs a new incident, the one long form here.
  const [isLoggingNew, setIsLoggingNew] = useState(false);
  const mainMode: WritingMode = chosenMode ?? (aiSummaries === 'off' ? 'plain' : 'ai');

  const goToStep = (next: Step): void => {
    setStepDirection(STEP_ORDER[next] < STEP_ORDER[step] ? 'back' : 'forward');
    setStep(next);
  };

  // Opening is a fresh form every time; last time's products are the starting point, not last time's answers.
  useEffect(() => {
    if (!show) return;
    setTab('work');
    setStep('hours');
    setStepDirection(null);
    setSplitOpen(false);
    setChosenMode(undefined);
    setSelected([]);
    setHours({});
    setErrors({});
    setNote(undefined);
    setSaved(undefined);
    setDescriptions({});
    setDraftedNothing(false);
  }, [show, day]);

  useEffect(() => {
    if (show && defaults) {
      setSelected(previous => (previous.length ? previous : defaults.products.filter(name => open.includes(name))));
    }
  }, [show, defaults, open]);

  // Officient time off still to be filed counts toward the day, as does what is already on it.
  const pendingAbsences = React.useContext(PendingAbsencesContext);
  const targetHours = useMemo(() => {
    const timeOff = pendingAbsences.filter(absence => absence.workday === day).reduce((sum, a) => sum + a.minutes, 0);

    return targetAfterTimeOff(timeOff + reportedMinutes);
  }, [pendingAbsences, day, reportedMinutes]);
  const effectiveHours = useMemo(
    () => withAutofilledHours(selected, hours, targetHours),
    [hours, selected, targetHours],
  );
  const isMissingHours = selected.some(product => !(effectiveHours[product] ?? '').trim());
  const total = selected.reduce(
    (sum, product) => sum + (Number((effectiveHours[product] ?? '').replace(',', '.')) || 0),
    0,
  );

  const submit = async (): Promise<void> => {
    const empty = selected.filter(product => (descriptions[product] ?? '').replace(/[-*•]/g, '').trim() === '');
    if (empty.length > 0) {
      toast.error(t('phillReport.describeEach'));
      return;
    }

    const writes = selected
      .map(product => products.find(candidate => candidate.name === product))
      // A product written by an earlier press of Submit is on the day now, so a retry leaves it alone.
      .filter((entity): entity is NonNullable<typeof entity> => !!entity && !taken.includes(entity.name))
      .map(entity =>
        onSubmit({
          userId,
          productId: entity.id,
          role: defaults?.chapter,
          workday,
          today: descriptions[entity.name],
          hours: Number((effectiveHours[entity.name] ?? '').replace(',', '.')),
        } as Partial<ReportEntity>),
      );

    setSaving(true);
    try {
      await Promise.all(writes);
      onHide();
    } catch {
      // The API's own message is already on screen. Staying open keeps what was typed.
    } finally {
      setSaving(false);
    }
  };

  const continueWith = async (writing: WritingMode): Promise<void> => {
    setSplitOpen(false);
    setSaved(undefined);
    const result = checkHours(selected, effectiveHours, t, targetHours);
    setErrors(result.errors);
    if (Object.keys(result.errors).length > 0) return;

    // Asked once. Pressing Continue again on the same total goes through, as it does in Slack.
    if (result.note && result.note !== note) {
      setNote(result.note);
      return;
    }

    setChapterKey(defaults?.chapter);

    if (writing === 'plain') {
      await submit();
      return;
    }

    goToStep('drafting');
    // The API drafts nothing while the setting is off; a failed save is already on screen as the global error toast.
    if (aiSummaries !== 'on') await saveDrafting(true).catch(() => undefined);
    const { drafts } = await draftReport({ organizationId, productNames: selected, workday }).catch(() => ({
      drafts: {},
    }));

    setDescriptions(
      Object.fromEntries(
        selected.map(product => [product, (drafts?.[product] ?? []).map(line => `- ${line}`).join('\n')]),
      ),
    );
    setDraftedNothing(selected.every(product => (drafts?.[product] ?? []).length === 0));
    goToStep('review');
  };

  const submitIncident = async (values: Partial<ReportEntity>): Promise<void> => {
    await onSubmit(values);
    incidentHours.current = Number(values.hours) || 0;
  };
  // The incident form closes itself on Cancel and after a save; only the save goes back to Standard work.
  const leaveIncident = (): void => {
    if (incidentHours.current === null) {
      onHide();
      return;
    }
    setSaved(t('phillReport.incidentSaved', { hours: hoursLabel(incidentHours.current) }));
    incidentHours.current = null;
    setTab('work');
  };
  const incidentPrefill = useMemo(() => ({ workday }), [workday]);

  const reviewHint = (): React.ReactNode => {
    if (!draftedNothing) return t('phillReport.reviewHint');

    return (
      <>
        <strong className="font-medium text-foreground">{t('phillReport.nothingFound')}</strong>{' '}
        {t('phillReport.nothingFoundHint')}
      </>
    );
  };

  const drafting = draftingLineFor(workday);
  const stepAnimation = stepDirection ? STEP_ANIMATION[stepDirection] : undefined;
  const shownDay = localDay(workday);
  const onFirstStep = step === 'hours';
  const showsGear = onFirstStep && tab === 'work' && mainMode === 'ai';

  // Fits three products, or one with its text box; two text boxes, a new incident or a long list take a bigger size.
  const onWorkStep = tab === 'work' && onFirstStep;
  const isTall = tab === 'incident' && isLoggingNew;
  const isWritten = onWorkStep && mainMode === 'plain' && selected.length > 1;
  const isLongList = onWorkStep && mainMode === 'ai' && selected.length > 3;
  const dialogHeight = cn(
    'h-[min(474px,calc(100dvh-2rem))]',
    isLongList && 'h-[min(620px,calc(100dvh-2rem))]',
    isWritten && 'h-[min(668px,calc(100dvh-2rem))]',
    isTall && 'h-[min(872px,calc(100dvh-2rem))]',
  );

  return (
    <Dialog
      open={show}
      onOpenChange={(next, details) => {
        if (next) return;
        // Escape folds the split button first, as it would a menu, rather than closing the whole report.
        if (splitOpen && details?.reason === 'escape-key') {
          setSplitOpen(false);
          return;
        }
        onHide();
      }}
    >
      {/* A fixed height, so adding products or switching tabs never resizes the window under the pointer. */}
      {/* Closing matches the backdrop's 100ms: a longer exit outlives its fade and the blur snaps back. */}
      <DialogContent
        className={`flex ${dialogHeight} flex-col transition-[height] duration-200 ease-out data-closed:duration-100 motion-reduce:transition-none sm:-translate-y-[max(30vh,50%)] sm:max-w-lg max-sm:[&>[data-slot=dialog-close]]:self-end`}
      >
        <DialogHeader className="gap-1 pr-16">
          <DialogTitle>
            {t('phillReport.title', { day: format(shownDay, 'EEEE') })}
            <span className="ml-2 text-sm font-normal text-muted-foreground">{format(shownDay, 'd MMM yyyy')}</span>
          </DialogTitle>
        </DialogHeader>
        <Presence
          show={showsGear}
          className="absolute top-2 right-10"
          enter="animate-in fade-in-0 duration-150"
          exit="animate-out fade-out-0 duration-150"
        >
          <DraftSettings
            membershipId={membership?.id}
            sources={readingSources.filter(source => source.key !== 'instructions')}
          />
        </Presence>

        {onFirstStep ? (
          <TabBar activeKey={tab} fadeClassName="from-popover">
            {(['work', 'incident'] as const).map(kind => (
              <button
                key={kind}
                type="button"
                aria-pressed={kind === tab}
                className={tabClass(kind === tab)}
                onClick={() => {
                  setTab(kind);
                  setSaved(undefined);
                }}
              >
                {t(kind === 'work' ? 'reportModal.standardWork' : 'reportModal.incidentWork')}
              </button>
            ))}
          </TabBar>
        ) : null}

        {onFirstStep && tab === 'incident' ? (
          <ReportIncidentForm
            show={show}
            onHide={leaveIncident}
            userId={userId}
            prefill={incidentPrefill}
            onSubmit={submitIncident}
            fill
            hideWorkday
            hideDescription
            showAhead
            onLoggingNewChange={setIsLoggingNew}
          />
        ) : (
          <>
            {/* Keyed on the step so each one mounts fresh and its entry animation fires. */}
            <div key={step} className={`flex min-h-0 flex-1 flex-col gap-4 ${stepAnimation ?? ''}`}>
              {step === 'hours' && (
                <>
                  {saved ? <p className="text-sm text-emerald-600 dark:text-emerald-400">{saved}</p> : null}
                  <div role="group" aria-labelledby="phill-worked-on" className="flex flex-col gap-1.5">
                    <p id="phill-worked-on" className="text-sm font-medium">
                      {t('phillReport.workedOn')}
                    </p>
                    <ProductPicker
                      products={open}
                      pills={pills}
                      disabled={taken}
                      disabledTitle={t('phillReport.alreadyReportedOn', { day: format(shownDay, 'EEEE') })}
                      selected={selected}
                      onChange={setSelected}
                    />
                  </div>

                  {/* `-mx-1 px-1` is the focus-ring gutter: scrolling would clip the ring off flat. */}
                  <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-1 pb-1">
                    {ordered.map(product => (
                      <div key={product} className="flex flex-col gap-1">
                        <div
                          className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-card px-3 py-2 ${isAbsenceProduct(product) ? 'bg-day-off' : ''}`}
                        >
                          <Label htmlFor={`phill-hours-${product}`} className="min-w-0 flex-1 truncate">
                            {product}
                          </Label>
                          <div className="relative">
                            <Input
                              id={`phill-hours-${product}`}
                              inputMode="decimal"
                              className={cn(
                                'h-8 w-20 pr-6 text-right tabular-nums',
                                isAbsenceProduct(product) && 'bg-card dark:bg-card',
                              )}
                              value={effectiveHours[product] ?? ''}
                              onChange={event => {
                                setHours(previous => ({ ...previous, [product]: event.target.value }));
                                setErrors(previous =>
                                  Object.fromEntries(Object.entries(previous).filter(([name]) => name !== product)),
                                );
                              }}
                              placeholder="0"
                            />
                            <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-sm text-muted-foreground">
                              h
                            </span>
                          </div>
                          {mainMode === 'plain' ? (
                            <Textarea
                              id={`phill-desc-${product}`}
                              aria-label={t('phillReport.describeProduct', { product })}
                              className="min-h-32 basis-full bg-card dark:bg-card"
                              placeholder={t('phillReport.describePlaceholder')}
                              value={descriptions[product] ?? ''}
                              onChange={event =>
                                setDescriptions(previous => ({ ...previous, [product]: event.target.value }))
                              }
                            />
                          ) : null}
                        </div>
                        {errors[product] ? <p className="text-sm text-destructive">{errors[product]}</p> : null}
                      </div>
                    ))}
                    {selected.length > 0 ? (
                      <p className="text-xs text-muted-foreground">{t('phillReport.hoursFormat')}</p>
                    ) : null}
                    {note ? (
                      <p className="text-sm text-foreground">
                        {t(note === 'long' ? 'phillReport.longDay' : 'phillReport.shortDay', { total })}
                      </p>
                    ) : null}
                  </div>
                </>
              )}

              {step === 'drafting' && (
                <div className="flex flex-col items-center gap-3 overflow-y-auto py-6 text-center">
                  <PhilBotIcon className="size-8 animate-report-bot-drift text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">{t(drafting.key, { date: drafting.date })}</p>
                  {sourcesRead.length > 0 ? (
                    <div className="text-left">
                      <p className="animate-report-source-in mb-1.5 text-xs text-muted-foreground">
                        {t('phillReport.readingFrom')}
                      </p>
                      <dl className="grid grid-cols-[auto_1fr] items-center gap-x-2.5 gap-y-1.5 text-left text-xs">
                        {sourcesRead.map((source, index) => (
                          <React.Fragment key={source.key}>
                            <dt
                              className="animate-report-source-in flex justify-center text-muted-foreground"
                              style={{ animationDelay: `${(index + 1) * 90}ms` }}
                            >
                              <source.Icon className="size-4" />
                              <span className="sr-only">{source.label}</span>
                            </dt>
                            {/* `break-all`: a repository name has no spaces to wrap on and would push the dialog wide. */}
                            <dd
                              className="animate-report-source-in break-all text-foreground"
                              style={{ animationDelay: `${(index + 1) * 90}ms` }}
                            >
                              {source.hover ? (
                                <Tooltip>
                                  {/* A button, not a span with `tabIndex`: without it the note is hover-only. */}
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="cursor-default text-left underline decoration-dotted underline-offset-2"
                                    >
                                      {source.detail}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>{source.hover}</TooltipContent>
                                </Tooltip>
                              ) : (
                                source.detail
                              )}
                            </dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    </div>
                  ) : null}
                </div>
              )}

              {step === 'review' && (
                <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1 pb-1">
                  <p className="text-sm text-muted-foreground">{reviewHint()}</p>
                  {chapterName ? (
                    <p className="text-xs text-muted-foreground">
                      {t('phillReport.reportedAs', { chapter: chapterName })}
                    </p>
                  ) : null}
                  {ordered.map(product => (
                    <div key={product} className="flex flex-col gap-1.5">
                      <Label htmlFor={`phill-desc-${product}`}>
                        {product} - {effectiveHours[product]} h
                      </Label>
                      <Textarea
                        id={`phill-desc-${product}`}
                        rows={3}
                        value={descriptions[product] ?? ''}
                        onChange={event =>
                          setDescriptions(previous => ({ ...previous, [product]: event.target.value }))
                        }
                      />
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">{t('phillReport.reviewFooter')}</p>
                </div>
              )}
            </div>

            <DialogFooter className="sm:items-start">
              {step === 'review' ? (
                <>
                  <Button variant="outline" onClick={() => goToStep('hours')}>
                    {t('back')}
                  </Button>
                  <Button onClick={submit} loading={saving} disabled={saving}>
                    {t('phillReport.submit')}
                  </Button>
                </>
              ) : (
                <>
                  {step === 'hours' ? (
                    <span className="self-center text-sm text-muted-foreground tabular-nums sm:mr-auto">
                      {t('phillReport.runningTotal', { total: hoursLabel(total), left: hoursLabel(targetHours) })}
                    </span>
                  ) : null}
                  <Button variant="outline" onClick={onHide}>
                    {t('actions.cancel')}
                  </Button>
                  {step === 'hours' ? (
                    <ContinueButton
                      main={mainMode}
                      onContinue={continueWith}
                      onChoose={chooseMode}
                      open={splitOpen}
                      onOpenChange={setSplitOpen}
                      disabled={selected.length === 0 || isMissingHours || saving}
                    />
                  ) : (
                    <Button disabled loading>
                      {t('phillReport.continue')}
                    </Button>
                  )}
                </>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhillReportDialog;
